import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, AlertTriangle, FileText, CheckCircle2, Copy, Check, 
  Download, ArrowLeft, Home, Sparkles, RefreshCw, Upload, X, 
  Layers, Scale, Building2, Calculator, ChevronRight, FileCheck, Landmark
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import { formatPriceWithCurrency, Currency } from '../utils/i18n';

interface TaxPnUnblockerAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome: () => void;
  currency?: Currency;
  currentUserEmail?: string;
}

type ActionMode = 'unblock_explanation' | 'risk_diagnosis' | 'data_table_1165' | 'dps_complaint' | 'doc_registry';

interface AttachedFile {
  name: string;
  size: number;
  type: string;
  contentSnippet: string;
}

export default function TaxPnUnblockerAgentView({
  language,
  onBack,
  onGoHome,
  currency = 'UAH',
  currentUserEmail
}: TaxPnUnblockerAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [activeMode, setActiveMode] = useState<ActionMode>('unblock_explanation');

  // Input Fields (Clean by default)
  const [field1, setField1] = useState(''); // Invoice No, Date, Sum, VAT
  const [field2, setField2] = useState(''); // UKT ZED / DKPP Code & Goods description
  const [field3, setField3] = useState(''); // Risk Criterion under Resolution 1165
  const [field4, setField4] = useState(''); // Primary documents & supply chain
  const [field5, setField5] = useState(''); // Material assets 20-OPP & employees
  const [field6, setField6] = useState(''); // Counterparties EDRPOU & Buyer/Supplier details
  const [notes, setNotes] = useState('');   // Specific instructions or receipt text

  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'docx' | 'checklist'>('preview');

  // 1-Click Trilingual Demo Presets
  const handleFillDemo = (mode = activeMode) => {
    if (mode === 'unblock_explanation') {
      if (isPl) {
        setField1('Faktura VAT (PN) Nr 148 z dnia 12.08.2026 / Wartość: 1 450 000.00 UAH (w tym VAT 20%: 241 666.67 UAH)');
        setField2('Kod towaru CN/UKT ZED 7214 20 00 00 (Pręty zbrojeniowe ze stali Ø12 mm) / Dostawa materiałów budowlanych');
        setField3('pkt 1 Kryteriów ryzyka operacji wg uchwały KMU 1165 (nadwyżka wolumenu dostawy nad wolumenem nabycia w wirtualnym stanie magazynowym DPS > 1.5)');
        setField4('Umowa dostawy nr 28/07-26 z dnia 28.07.2026, Specyfikacja nr 1, Faktura WZ nr 148, List przewozowy TTN nr 148-T, Certyfikat jakości nr UA.041.00248, Wyciąg bankowy nr 892');
        setField5('Formularz 20-OPP złożony (magazyn stali 1 200 m², umowa najmu), wózek widłowy, dźwig, 18 zatrudnionych pracowników, brak zaległości podatkowych i ZUS');
        setField6('Nabywca: TOV «Kyiv-Bud Development» (EDRPOU 39485721, czynny płatnik VAT) ↔ Dostawca: TOV «Met-Prom Trade» (EDRPOU 41829304)');
        setNotes('Przygotować wyczerpujące oficjalne pisemne wyjaśnienia wraz z uzasadnieniem dokumentacyjnym do Komisji Regionalnej DPS w celu odblokowania i rejestracji faktury VAT (PN) nr 148 z dnia 12.08.2026. Wykazać realność transakcji gospodarczej, obalić kryterium ryzyka wg Uchwały KMU 1165 z powołaniem się na linię orzeczniczą Sądu Najwyższego oraz wygenerować rejestr dokumentów źródłowych w formacie Word (.docx).');
      } else if (isEn) {
        setField1('VAT Invoice No. 148 dated 12.08.2026 / Total Amount: 1,450,000.00 UAH (incl. 20% VAT: 241,666.67 UAH)');
        setField2('HS/UKT ZED Code 7214 20 00 00 (Hot-rolled steel rebar Ø12 mm) / Supply of construction materials');
        setField3('p. 1 Transaction Risk Criteria under CMU Res. 1165 (Volume of supplied goods exceeds purchase volume in DPS virtual balance by > 1.5x)');
        setField4('Supply Contract No. 28/07-26 dtd 28.07.2026, Spec No. 1, Delivery Note VN-148, Consignment Note TTN-148-T, Quality Certificate UA.041.00248, Bank Payment Order 892, Manufacturer Source Invoices');
        setField5('Form 20-OPP filed (1,200 sq.m metal warehouse under lease No. 12/24), forklift, crane, 18 employees on payroll, zero tax or salary arrears');
        setField6('Buyer: LLC «Kyiv-Bud Development» (EDRPOU 39485721, Active VAT Payer) ↔ Supplier: LLC «Met-Prom Trade» (EDRPOU 41829304)');
        setNotes('Draft comprehensive official written Explanations with documentary evidence to the Regional Commission of the State Tax Service (DPS) to unblock and register VAT invoice No. 148 dated 12.08.2026. Prove economic substance and genuine commercial reality, refute p. 1 Transaction Risk Criteria under Resolution No. 1165 with Supreme Court legal precedents and generate a structured Primary Documents Register in Word (.docx DSTU 4163:2020).');
      } else {
        setField1('Податкова накладна № 148 від 12.08.2026 / Сума: 1 450 000.00 грн (в т.ч. ПДВ 20%: 241 666.67 грн)');
        setField2('Код УКТ ЗЕД 7214 20 00 00 (Арматура сталева гарячекатана Ø12 мм) / Поставка за договором купівлі-продажу будівельних матеріалів');
        setField3('п. 1 Критеріїв ризиковості здійснення операцій (Перевищення обсягу постачання товару 7214 над обсягом придбання у віртуальному залишку ДПС > 1.5 разу)');
        setField4('Договір поставки № 28/07-26 від 28.07.2026, Специфікація № 1, Видаткова накладна № ВН-148 від 12.08.2026, ТТН № 148-Т від 12.08.2026 (перевізник ТОВ «Транс-Буд»), Сертифікат відповідності № UA.041.00248, Платіжне доручення № 892 від 13.08.2026, Вхідні видаткові накладні від заводу-виробника ПрАТ «АрселорМіттал Кривий Ріг»');
        setField5('Форма 20-ОПП подана (склад металопрокату 1 200 кв.м, договір оренди № 12/24), кран баштовий, навантажувач вилковий, штатна чисельність — 18 працівників (включаючи водіїв, стропальників, комірників), заборгованість із заробітної плати та ЄСВ відсутня');
        setField6('Покупець: ТОВ «Київ-Буд Девелопмент» (ЄДРПОУ 39485721, платник ПДВ, генпідрядник житлового комплексу) ↔ Постачальник: ТОВ «Мет-Пром Трейд» (ЄДРПОУ 41829304)');
        setNotes('Скласти вичерпне офіційне Письмове Пояснення з документальним обґрунтуванням до Комісії регіонального рівня ГУ ДПС для прийняття рішення про реєстрацію податкової накладної № 148 від 12.08.2026. Довести реальність господарської операції (наявність джерела походження товару, транспортування, складського зберігання, наявності трудових та матеріальних ресурсів за ф. 20-ОПП), спростувати п. 1 Критеріїв ризиковості за Постановою КМУ № 1165 з посиланням на правові висновки Верховного Суду (постанови у справах № 826/12108/18, № 810/3947/18, № 160/8860/21) та сформувати Реєстр первинних документів для електронного подання (форма J/F1312603) у Word (.docx за ДСТУ 4163:2020).');
      }
    } else if (mode === 'risk_diagnosis') {
      setField1('Планова ПН на суму 980 000.00 грн (в т.ч. ПДВ 163 333.33 грн) / Розрахунковий період: Серпень 2026');
      setField2('Код УКТ ЗЕД 1001 99 00 00 (Пшениця 2 класу м\'яка) / Обсяг постачання 120 тонн');
      setField3('Перевірка критеріїв безумовної реєстрації за п. 3 Порядку № 1165: розрахунок показників D (податкове навантаження) та P (коефіцієнт приросту ПДВ)');
      setField4('Вхідний податковий кредит: закупівля у с/г товаровиробників (ф. 1-ДФ, договори купівлі-продажу, ТТН на вивезення з току, складські квитанції елеватора)');
      setField5('Орендований зерносклад 3 500 кв.м (ф. 20-ОПП № 4812 від 10.02.2025), власний вантажний автотранспорт (3 зерновози MAN), 12 працівників');
      setField6('Покупець: ПАТ «Державна Продовольчо-Зернова Корпорація» (ЄДРПОУ 37243279) ↔ Постачальник: ТОВ «Агро-Експорт Трейд» (ЄДРПОУ 43198512)');
      setNotes('Провести превентивний стрес-тест планової податкової накладної перед реєстрацією в ЄРПН. Розрахувати показник D (чи перевищує 0.05), показник P (чи менший за P_max), перевірити віртуальний залишок пшениці за кодом 1001 в системі СМКОР та сформувати рекомендації щодо усунення ризику зупинення.');
    } else if (mode === 'data_table_1165') {
      setField1('Таблиця даних платника ПДВ (Форма J/F1312303) / Код виду діяльності за КВЕД-2010: 25.11 «Виробництво будівельних металевих конструкцій»');
      setField2('Коди на придбанні (вхідні): 7214, 7216, 7306 (прокат, швелери, труби) ➔ Коди на постачанні (вихідні): 7308 90 59 00, 7308 90 98 00 (металоконструкції каркасів будівель)');
      setField3('Попереднє рішення Комісії ДПС: «Неврахування Таблиці даних через відсутність інформації щодо наявності виробничих потужностей»');
      setField4('Договір оренди виробничого цеху № 15/22 від 01.03.2022 (площа 2 400 кв.м), акти приймання-передачі верстатів плазмової різки, зварювальних апаратів, кран-балки');
      setField5('Форма 20-ОПП зареєстрована (об\'єкт № 1: виробничий цех, об\'єкт № 2: склад готової продукції), 34 працівники (зварювальники, слюсарі, інженери-конструктори), сплата ПДФО/ЄСВ — 145 000 грн/міс');
      setField6('ТОВ «Завод Будівельних Металоконструкцій» (ЄДРПОУ 42095814, платник ПДВ з 2018 року)');
      setNotes('Сформувати вичерпне обґрунтування (Пояснювальну записку) до Таблиці даних платника податку за формою J/F1312303 відповідно до вимог п. 12-14 Порядку № 1165. Детально описати повний технологічний виробничий цикл перетворення вхідної сировини (прокату 7214/7216) у готові будівельні металоконструкції (код 7308), підтвердити матеріально-технічну базу та кваліфікований персонал.');
    } else if (mode === 'dps_complaint') {
      setField1('Рішення Комісії ГУ ДПС про відмову в реєстрації ПН № 7491204/41829304 від 20.08.2026 щодо ПН № 148 від 12.08.2026 на суму 1 450 000.00 грн');
      setField2('Підстава відмови за формою J/F1412403: «Надання платником податку копій документів, складених із порушенням законодавства / ненадання документів щодо транспортування»');
      setField3('Спростування незаконного рішення: первинні документи подані в повному обсязі, ТТН містить усі обов\'язкові реквізити за ЗУ «Про бухгалтерський облік» та Правилами перевезень вантажів');
      setField4('Пакет документів, що долучається: Копія ПН № 148, Квитанція про зупинення, Пояснення з додатками (вх. ДПС № 90481), Рішення про відмову, ТТН з підписами водія та вантажоодержувача, Акт наданих транспортних послуг, сертифікати якості');
      setField5('Дотримання строків: Скаргу подано протягом 10 робочих днів з дня отримання Рішення про відмову (відповідно до п. 56.3 ПКУ та Порядку розгляду скарг № 1165)');
      setField6('Скаржник: ТОВ «Мет-Пром Трейд» (ЄДРПОУ 41829304) ➔ Орган оскарження: Комісія Державної податкової служби України з питань розгляду скарг (м. Київ, Львівська пл., 8)');
      setNotes('Скласти мотивовану Адміністративну Скаргу на Рішення Комісії регіонального рівня про відмову в реєстрації ПН за формою J/F1313203. Навести нормативне спростування формальних підстав відмови, використати сталу судову практику Верховного Суду про те, що загальний бланкетний запис у рішенні ДПС без конкретизації дефекту є протиправним (справи № 826/12108/18, № 640/11488/20), та вимагати скасування рішення і обов\'язкової реєстрації ПН в ЄРПН.');
    } else {
      setField1('Пакет первинних документів до ПН № 148 від 12.08.2026 на суму 1 450 000.00 грн');
      setField2('Код УКТ ЗЕД 7214 20 00 00 (Арматура сталева)');
      setField3('Формування реєстру та перевірка обов\'язкових реквізитів згідно зі ст. 9 ЗУ «Про бухгалтерський облік та фінансову звітність в Україні»');
      setField4('1. Договір поставки з додатками; 2. Специфікація; 3. Рахунок-фактура; 4. Видаткова накладна; 5. ТТН; 6. Банківська виписка; 7. Вхідні документи придбання; 8. Сертифікат виробника');
      setField5('Форма 20-ОПП, договір оренди складу, штатна відомість');
      setField6('ТОВ «Мет-Пром Трейд» ↔ ТОВ «Київ-Буд Девелопмент»');
      setNotes('Скласти деталізований Реєстр первинних документів для долучення до форми Повідомлення J/F1312603 із зазначенням номерів, дат, сторінок, типів файлів та їх юридичного призначення.');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    files.forEach((file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string || '';
        const snippet = text.slice(0, 1000);
        setAttachedFiles(prev => [
          ...prev,
          {
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream',
            contentSnippet: snippet
          }
        ]);
      };
      if (file.type.includes('text') || file.name.endsWith('.xml') || file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        reader.readAsText(file);
      } else {
        reader.readAsDataURL(file);
      }
    });
  };

  const handleRemoveFile = (idx: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    if (!field1.trim() && !field2.trim() && !notes.trim()) {
      setError(isPl 
        ? 'Wprowadź dane faktury VAT (PN/RK) lub kliknij «⚡ Wypełnij przykładem 1 kliknięciem».'
        : isUk 
        ? 'Будь ласка, заповніть дані ПН/РК або скористайтеся кнопкою «⚡ Заповнити демо-прикладом у 1 клік».'
        : 'Please provide Tax Invoice details or click «⚡ Fill Demo Preset in 1-Click».');
      return;
    }

    setError(null);
    setIsGenerating(true);
    setReport(null);

    const modeTitles: Record<ActionMode, string> = {
      unblock_explanation: 'Письмові Пояснення для Розблокування ПН/РК (Форма J/F1312603)',
      risk_diagnosis: 'Експрес-Діагностика та Стрес-Тест Ризику Блокування ПН/РК (ПКМУ № 1165)',
      data_table_1165: 'Таблиця Даних Платника Податків з Пояснювальною Запискою (Форма J/F1312303)',
      dps_complaint: 'Адміністративна Скарга на Рішення про Відмову в Реєстрації ПН (Форма J/F1313203)',
      doc_registry: 'Реєстр та Чек-лист Первинних Документів до Пояснень СМКОР'
    };

    const payload = {
      agentId: 'tax-pn-unblocker-auditor',
      userQuery: `[РЕЖИМ ОБРОБКИ СМКОР: ${modeTitles[activeMode].toUpperCase()}]\n${notes || 'Скласти офіційний нормативно обґрунтований документ відповідно до Постанови КМУ № 1165 та ДСТУ 4163:2020.'}`,
      language,
      parameters: {
        "Цільовий документ / Режим": modeTitles[activeMode],
        "Реквізити ПН/РК та сума з ПДВ": field1 || "Згідно з доданими матеріалами",
        "Код УКТ ЗЕД / ДКПП та номенклатура": field2 || "Згідно з договором",
        "Критерій зупинення за ПКМУ № 1165 / Ризик": field3 || "п. 1 ризиковості операцій",
        "Первинні документи та ланцюг постачання": field4 || "Договір, ТТН, видаткова, оплата",
        "Матеріально-технічна база за 20-ОПП та штат": field5 || "Форма 20-ОПП зареєстрована",
        "Контрагенти (Покупець / Постачальник)": field6 || "Платники ПДВ",
        "Додаткові документи та файли": attachedFiles.map(f => `${f.name} (${Math.round(f.size/1024)} KB)`).join(', ')
      }
    };

    try {
      const res = await fetch('/api/generate-agent-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.reason || 'Збій сервера при формуванні звіту');
      }

      const generatedContent = data.report || data.content || '';
      setReport(generatedContent);

      // Auto download Word .docx
      if (generatedContent.length > 200) {
        const filePrefix = activeMode === 'unblock_explanation' ? 'Poyasnennya_Rozblokuvan_PN'
          : activeMode === 'risk_diagnosis' ? 'Audit_Ryzikiv_SMKOR'
          : activeMode === 'data_table_1165' ? 'Tablytsya_Danykh_1165'
          : activeMode === 'dps_complaint' ? 'Skarga_DPS_Vidmova_PN'
          : 'Reyestr_Pervynnykh_Dokumentiv';
        autoDownloadReport(generatedContent, `${filePrefix}_${new Date().toISOString().slice(0, 10)}`, 'tax-pn-unblocker');
      }
    } catch (err: any) {
      console.error('Error generating tax report:', err);
      setError(err.message || 'Помилка генерації документа');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadWord = () => {
    if (!report) return;
    autoDownloadReport(report, `Poyasnennya_Rozblokuvan_PN_${new Date().toISOString().slice(0, 10)}`, 'tax-pn-unblocker');
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-emerald-500/30 rounded-2xl p-4 text-white shadow-xl">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-emerald-300 text-xs font-extrabold uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-emerald-500/40"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Centrum Podatkowego' : isUk ? '← Назад до Податкового Центру' : '← Back to Tax Hub'}</span>
          </button>

          <button
            onClick={onGoHome}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
          >
            <Home className="w-3.5 h-3.5" />
            <span>{isPl ? 'Główna' : isUk ? 'Головна' : 'Home'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1 text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 rounded-lg flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>СМКОР & ПОСТАНОВА КМУ № 1165</span>
          </span>
          <span className="px-3 py-1 text-[10px] font-black uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 rounded-lg">
            ДСТУ 4163:2020 • ПКУ ст. 201
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border-2 border-emerald-500/60 rounded-3xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 bottom-0 w-64 h-64 bg-teal-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 max-w-4xl space-y-3.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 text-[9px] font-black uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span>ФЛАГМАНСЬКИЙ АНТИБЛОКУВАЛЬНИЙ МОДУЛЬ ДПС</span>
            </span>
            <span className="px-3 py-1 text-[9px] font-black uppercase bg-amber-500/30 text-amber-300 border border-amber-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
              <span>⚡ 100% ДОТРИМАННЯ СУДОВОЇ ПРАКТИКИ ВЕРХОВНОГО СУДУ</span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black uppercase text-white tracking-tight leading-tight">
            🛡️ Розблокування Податкових Накладних (ПН/РК) та Протидія СМКОР (ПКМУ № 1165)
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
            {isPl
              ? 'Profesjonalny moduł AI do przeciwdziałania wstrzymaniu rejestracji faktur VAT (PN/RK) zgodnie z Uchwałą Rady Ministrów nr 1165. Analizuje kryteria ryzyka, generuje pisemne wyjaśnienia do Urzędu Skarbowego (DPS), przygotowuje Tabele Danych Podatnika (J/F1312303) oraz skargi administracyjne z powołaniem na orzecznictwo Sądu Najwyższego w Word (.docx).'
              : isUk
              ? 'Професійний комплексний AI-модуль захисту від блокування ПН/РК за Постановою Кабміну № 1165. Здійснює експрес-діагностику ризиків, формує юридично бездоганні Письмові Пояснення (форма J/F1312603), складає Таблицю даних платника (J/F1312303), Адміністративну Скаргу до ДПС України (J/F1313203) та реєстр первинних документів за ДСТУ 4163:2020.'
              : 'Enterprise-grade AI module for countering VAT invoice (PN/RK) suspension under CMU Resolution No. 1165. Audits transaction risk criteria, generates official written Explanations to DPS (Form J/F1312603), Taxpayer Data Tables (J/F1312303), Administrative Appeals, and primary document registries in Word (.docx DSTU 4163:2020).'}
          </p>
        </div>
      </div>

      {/* Mode Selector Tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
        {[
          { id: 'unblock_explanation', name: '📄 Письмові Пояснення (J/F1312603)', desc: 'Розблокування зупиненої ПН' },
          { id: 'risk_diagnosis', name: '🛡️ Експрес-Діагностика Ризиків', desc: 'Пре-чек D і P перед відправкою' },
          { id: 'data_table_1165', name: '📊 Таблиця Даних (J/F1312303)', desc: 'Коди УКТ ЗЕД + Обґрунтування' },
          { id: 'dps_complaint', name: '⚖️ Скарга на Відмову (J/F1313203)', desc: 'Оскарження до ДПС України' },
          { id: 'doc_registry', name: '📑 Реєстр Первинних Документів', desc: 'Чек-лист та опис сканів' },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => {
              setActiveMode(m.id as ActionMode);
              if (field1 || field2) {
                handleFillDemo(m.id as ActionMode);
              }
            }}
            className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
              activeMode === m.id
                ? 'bg-emerald-950/80 border-emerald-400 text-white shadow-lg shadow-emerald-600/20'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800 hover:border-slate-700'
            }`}
          >
            <div className="text-xs font-black uppercase text-slate-100">{m.name}</div>
            <div className="text-[10px] text-emerald-400/80 mt-1 font-sans">{m.desc}</div>
          </button>
        ))}
      </div>

      {/* Input Parameters Form */}
      <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-6 sm:p-8 text-white shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
          <div>
            <h2 className="text-sm font-extrabold uppercase text-emerald-200 tracking-wider flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-emerald-400" />
              <span>Параметри Господарської Операції та Зупиненої ПН/РК</span>
            </h2>
            <p className="text-[11px] text-slate-400 mt-0.5 font-sans">
              Поля ініціалізовані чистими. Ви можете ввести власні дані або скористатися кнопкою демо-заповнення.
            </p>
          </div>

          <button
            type="button"
            onClick={() => handleFillDemo()}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-slate-950 font-black text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer hover:scale-[1.02]"
          >
            <Sparkles className="w-3.5 h-3.5 text-slate-950" />
            <span>⚡ Заповнити демо-прикладом у 1 клік</span>
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-950/60 border border-red-500/50 rounded-xl text-red-200 text-xs flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Field 1 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>1. Реквізити ПН/РК, дата та сума з ПДВ:</span>
            </label>
            <input
              type="text"
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              placeholder="напр. ПН № 148 від 12.08.2026 / Сума 1 450 000.00 грн (в т.ч. ПДВ 241 666.67 грн)"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>

          {/* Field 2 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>2. Код УКТ ЗЕД / ДКПП та номенклатура товару/послуги:</span>
            </label>
            <input
              type="text"
              value={field2}
              onChange={(e) => setField2(e.target.value)}
              placeholder="напр. Код УКТ ЗЕД 7214 20 00 00 (Арматура сталева Ø12 мм) / Поставка за договором"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>

          {/* Field 3 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>3. Критерій ризиковості / Причина за ПКМУ № 1165:</span>
            </label>
            <input
              type="text"
              value={field3}
              onChange={(e) => setField3(e.target.value)}
              placeholder="напр. п. 1 Критеріїв ризиковості операцій (перевищення віртуального залишку > 1.5 разу)"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>

          {/* Field 4 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>4. Первинні документи та ланцюг постачання:</span>
            </label>
            <input
              type="text"
              value={field4}
              onChange={(e) => setField4(e.target.value)}
              placeholder="напр. Договір поставки, Специфікація, Видаткова накладна, ТТН, виписка банку, сертифікат"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>

          {/* Field 5 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>5. Матеріальна база (ф. 20-ОПП) та найманий персонал:</span>
            </label>
            <input
              type="text"
              value={field5}
              onChange={(e) => setField5(e.target.value)}
              placeholder="напр. ф. 20-ОПП зареєстрована (склад 1 200 кв.м), навантажувачі, 18 штатних працівників"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>

          {/* Field 6 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>6. Сторони операції (Покупець / Постачальник, ЄДРПОУ):</span>
            </label>
            <input
              type="text"
              value={field6}
              onChange={(e) => setField6(e.target.value)}
              placeholder="напр. Покупець: ТОВ «Київ-Буд» (ЄДРПОУ 39485721) ↔ Постачальник: ТОВ «Мет-Пром» (ЄДРПОУ 41829304)"
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
            />
          </div>
        </div>

        {/* Detailed Task Description / Notes */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
            <span>Текст квитанції про зупинення або спеціальні юридичні вимоги:</span>
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Вставте повний текст квитанції ДПС або вкажіть додаткові факти операції для посилення правової позиції..."
            className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-xl p-3.5 text-xs text-slate-100 font-mono"
          />
        </div>

        {/* File Upload Box */}
        <div className="space-y-2 pt-2 border-t border-slate-800">
          <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
            <span>Прикріпити квитанції ДПС / первинні документи (PDF, DOCX, XLSX, XML, JPG):</span>
            <span className="text-[10px] text-slate-500">{attachedFiles.length} файлів додано</span>
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-slate-950 hover:bg-slate-800 border border-dashed border-emerald-500/50 hover:border-emerald-400 rounded-xl text-xs text-emerald-300 font-bold uppercase cursor-pointer flex items-center gap-2 transition-all">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span>Обрати файли квитанцій</span>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.xml,.txt,.csv,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg text-xs text-slate-200">
                <FileText className="w-3.5 h-3.5 text-emerald-400" />
                <span className="max-w-[180px] truncate">{file.name}</span>
                <span className="text-[10px] text-slate-500">({Math.round(file.size / 1024)} KB)</span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(idx)}
                  className="text-slate-500 hover:text-red-400 cursor-pointer ml-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Action Button & Pricing */}
        <div className="pt-4 border-t border-slate-800 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="px-2.5 py-1 bg-slate-950 border border-slate-800 rounded-lg text-slate-300">
              ✨ Модель: <strong className="text-emerald-400">Gemini 3.7 Flash</strong>
            </span>
            <span className="px-2.5 py-1 bg-slate-950 border border-slate-800 rounded-lg text-slate-300">
              Тариф: <strong className="text-amber-300">500 грн / 30 запитів</strong> (разово: 25 грн)
            </span>
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-8 py-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-slate-950 font-black text-xs uppercase rounded-xl transition-all shadow-xl shadow-emerald-600/40 flex items-center gap-3 cursor-pointer hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-slate-950" />
                <span>Юридичний аналіз СМКОР та генерація...</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 text-slate-950" />
                <span>🚀 Згенерувати Пакет Розблокування у Word (.docx)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Generated Report Canvas */}
      {report && (
        <div className="bg-slate-900 border border-emerald-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-3">
            <div>
              <h3 className="text-base font-extrabold uppercase text-emerald-300 tracking-wider flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span>Офіційний Документ СМКОР Сформовано (ДСТУ 4163:2020)</span>
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Готовий до підписання КЕП та подання через Електронний кабінет платника
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Скопійовано!' : 'Копіювати'}</span>
              </button>

              <button
                onClick={handleDownloadWord}
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-slate-950 text-xs font-black uppercase rounded-lg transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-slate-950" />
                <span>Завантажити Word (.docx)</span>
              </button>

              <TelegramExportButton
                title="Розблокування ПН/РК (ПКМУ 1165)"
                content={report || ''}
                agentName="Розблокування ПН/РК (ПКМУ 1165)"
                isUk={language === 'uk'}
              />
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 font-sans text-xs text-slate-200 leading-relaxed overflow-x-auto max-h-[700px] overflow-y-auto whitespace-pre-wrap selection:bg-emerald-500 selection:text-slate-950">
            {report}
          </div>
        </div>
      )}
    </div>
  );
}
